import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { coachService } from '@/services/coach';
import { useAuthStore } from '@/store/auth';
import { Button, Input, Field, FieldLabel, FieldError } from '@/components/ui';
import { LEGAL_VERSIONS } from '@court-sync/shared';
import { GraduationCap, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

const coachSignUpSchema = z
    .object({
        firstName: z.string().min(1, 'First name is required'),
        lastName: z.string().min(1, 'Last name is required'),
        email: z.string().email('Please enter a valid email address'),
        password: z
            .string()
            .min(8, 'Password must be at least 8 characters')
            .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
            .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
            .regex(/[0-9]/, 'Password must contain at least one number'),
        confirmPassword: z.string(),
        termsAccepted: z.boolean().refine((val) => val === true, {
            message: 'You must accept the Terms of Service',
        }),
        privacyAccepted: z.boolean().refine((val) => val === true, {
            message: 'You must accept the Privacy Policy',
        }),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ['confirmPassword'],
    });

type CoachSignUpFormData = z.infer<typeof coachSignUpSchema>;

function parseCoachName(coachName: string): { firstName: string; lastName: string } {
    const parts = coachName.trim().split(/\s+/);
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';
    return { firstName, lastName };
}

type PageState = 'loading' | 'ready' | 'accepting' | 'success' | 'error' | 'expired' | 'already_accepted';

export default function CoachInviteAcceptPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { user, register: registerUser, refreshUser } = useAuthStore();

    const [pageState, setPageState] = useState<PageState>('loading');
    const [signUpError, setSignUpError] = useState<string | null>(null);
    const [isSigningUp, setIsSigningUp] = useState(false);

    const form = useForm<CoachSignUpFormData>({
        resolver: zodResolver(coachSignUpSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            termsAccepted: false,
            privacyAccepted: false,
        },
    });

    const [inviteInfo, setInviteInfo] = useState<{
        academyName: string;
        academyLogoUrl?: string;
        coachName: string;
        email: string;
    } | null>(null);
    const [academySlug, setAcademySlug] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!token) {
                if (mounted) { setPageState('error'); setErrorMsg('Invalid invite link'); }
                return;
            }
            try {
                const info = await coachService.getInviteDetails(token);
                if (!mounted) return;
                if (info.status === 'accepted') { setPageState('already_accepted'); return; }
                if (info.status === 'expired') { setPageState('expired'); return; }
                setInviteInfo(info);
                setPageState('ready');
            } catch (err) {
                if (!mounted) return;
                setPageState('error');
                setErrorMsg(err instanceof Error ? err.message : 'Unknown error');
            }
        })();
        return () => { mounted = false; };
    }, [token]);

    useEffect(() => {
        if (inviteInfo) {
            const { firstName, lastName } = parseCoachName(inviteInfo.coachName);
            form.reset({
                firstName: firstName || '',
                lastName: lastName || '',
                email: inviteInfo.email || '',
                password: '',
                confirmPassword: '',
                termsAccepted: false,
                privacyAccepted: false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when inviteInfo loads
    }, [inviteInfo]);

    const handleSignUp = async (data: CoachSignUpFormData) => {
        if (!token) return;
        setSignUpError(null);
        setIsSigningUp(true);
        try {
            const returnUrl = `/coach/invite/${token}`;
            await registerUser(
                data.email,
                data.password,
                `${data.firstName} ${data.lastName}`,
                'coach',
                undefined,
                returnUrl,
                {
                    termsAccepted: data.termsAccepted,
                    privacyAccepted: data.privacyAccepted,
                    termsVersion: LEGAL_VERSIONS.TERMS,
                    privacyVersion: LEGAL_VERSIONS.PRIVACY,
                }
            );
        } catch (err: unknown) {
            const authError = err as { code?: string };
            if (authError.code === 'auth/email-already-in-use') {
                const loginUrl = `/login?returnUrl=${encodeURIComponent(`/coach/invite/${token}`)}`;
                navigate(loginUrl, {
                    state: {
                        email: data.email,
                        message: 'An account with this email already exists. Please sign in to accept the invitation.',
                    },
                });
                return;
            }
            setSignUpError(err instanceof Error ? err.message : 'Failed to create account');
        } finally {
            setIsSigningUp(false);
        }
    };

    const handleAccept = async () => {
        if (!token) return;
        try {
            setPageState('accepting');
            const result = await coachService.acceptInvite(token);
            setAcademySlug(result.academySlug);
            await refreshUser();

            const currentUser = useAuthStore.getState().user;
            if (currentUser && !currentUser.isEmailVerified) {
                const returnUrl = result.academySlug
                    ? `/academy/${result.academySlug}/coach/dashboard`
                    : '/coach/academy-picker';
                navigate(
                    `/email-verification?email=${encodeURIComponent(currentUser.email ?? '')}&returnUrl=${encodeURIComponent(returnUrl)}`,
                    {
                        state: {
                            message: 'Please verify your email to complete your account setup.',
                            showEmailVerification: true,
                        },
                    }
                );
                return;
            }

            setPageState('success');
        } catch (err: unknown) {
            setPageState('error');
            const axiosError = err as { response?: { data?: { error?: string } } };
            setErrorMsg(
                axiosError.response?.data?.error ??
                    (err instanceof Error ? err.message : 'Failed to accept invitation')
            );
        }
    };

    const handleGoToDashboard = () => {
        if (academySlug) {
            navigate(`/academy/${academySlug}/coach/dashboard`);
        } else {
            navigate('/coach/academy-picker');
        }
    };

    return (
        <div className="min-h-screen bg-dashboard-canvas flex flex-col items-center justify-center px-4">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
                <div className="flex items-center justify-center mb-6">
                    {inviteInfo?.academyLogoUrl ? (
                        <img
                            src={inviteInfo.academyLogoUrl}
                            alt={`${inviteInfo.academyName} logo`}
                            className="h-16 w-auto max-w-[200px] object-contain"
                        />
                    ) : (
                        <div className="h-16 w-16 rounded-2xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <GraduationCap className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                        </div>
                    )}
                </div>

                {pageState === 'loading' && (
                    <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-3" />
                        <p className="text-gray-600 dark:text-gray-400">Validating invite link...</p>
                    </div>
                )}

                {pageState === 'ready' && inviteInfo && (
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                            You've been invited!
                        </h1>
                        <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
                            <span className="font-semibold text-gray-900 dark:text-white">{inviteInfo.academyName}</span> would like to add you as a coach on CourtSync.
                        </p>

                        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700 mb-6">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Invited as</p>
                            <p className="font-semibold text-gray-900 dark:text-white">{inviteInfo.coachName}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{inviteInfo.email}</p>
                        </div>

                        {!user ? (
                            <div className="space-y-4">
                                <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                                    Already have an account?{' '}
                                    <Link
                                        to={`/login?returnUrl=${encodeURIComponent(`/coach/invite/${token ?? ''}`)}`}
                                        className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                                    >
                                        Sign in
                                    </Link>
                                </p>

                                <form onSubmit={form.handleSubmit(handleSignUp)} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <Field data-invalid={!!form.formState.errors.firstName}>
                                            <FieldLabel htmlFor="coach-firstName">First Name</FieldLabel>
                                            <Input
                                                id="coach-firstName"
                                                {...form.register('firstName')}
                                                placeholder="First name"
                                                className="h-10"
                                            />
                                            <FieldError>{form.formState.errors.firstName?.message}</FieldError>
                                        </Field>
                                        <Field data-invalid={!!form.formState.errors.lastName}>
                                            <FieldLabel htmlFor="coach-lastName">Last Name</FieldLabel>
                                            <Input
                                                id="coach-lastName"
                                                {...form.register('lastName')}
                                                placeholder="Last name"
                                                className="h-10"
                                            />
                                            <FieldError>{form.formState.errors.lastName?.message}</FieldError>
                                        </Field>
                                    </div>
                                    <Field data-invalid={!!form.formState.errors.email}>
                                        <FieldLabel htmlFor="coach-email">Email</FieldLabel>
                                        <Input
                                            id="coach-email"
                                            type="email"
                                            {...form.register('email')}
                                            placeholder="Email"
                                            className="h-10"
                                            readOnly
                                            title="Use the email this invitation was sent to"
                                        />
                                        <FieldError>{form.formState.errors.email?.message}</FieldError>
                                    </Field>
                                    <Field data-invalid={!!form.formState.errors.password}>
                                        <FieldLabel htmlFor="coach-password">Password</FieldLabel>
                                        <Input
                                            id="coach-password"
                                            type="password"
                                            {...form.register('password')}
                                            placeholder="Create a password"
                                            className="h-10"
                                        />
                                        <FieldError>{form.formState.errors.password?.message}</FieldError>
                                    </Field>
                                    <Field data-invalid={!!form.formState.errors.confirmPassword}>
                                        <FieldLabel htmlFor="coach-confirmPassword">Confirm Password</FieldLabel>
                                        <Input
                                            id="coach-confirmPassword"
                                            type="password"
                                            {...form.register('confirmPassword')}
                                            placeholder="Confirm password"
                                            className="h-10"
                                        />
                                        <FieldError>{form.formState.errors.confirmPassword?.message}</FieldError>
                                    </Field>
                                    <div className="space-y-2">
                                        <label className="flex items-start gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                {...form.register('termsAccepted')}
                                                className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600"
                                            />
                                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                                I agree to the{' '}
                                                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                                                    Terms of Service
                                                </a>
                                            </span>
                                        </label>
                                        {form.formState.errors.termsAccepted && (
                                            <p className="text-sm text-red-600">{form.formState.errors.termsAccepted.message}</p>
                                        )}
                                        <label className="flex items-start gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                {...form.register('privacyAccepted')}
                                                className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600"
                                            />
                                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                                I agree to the{' '}
                                                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                                                    Privacy Policy
                                                </a>
                                            </span>
                                        </label>
                                        {form.formState.errors.privacyAccepted && (
                                            <p className="text-sm text-red-600">{form.formState.errors.privacyAccepted.message}</p>
                                        )}
                                    </div>
                                    {signUpError && (
                                        <p className="text-sm text-red-600 dark:text-red-400">{signUpError}</p>
                                    )}
                                    <Button type="submit" className="w-full" disabled={isSigningUp}>
                                        {isSigningUp ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                Creating account...
                                            </>
                                        ) : (
                                            'Create Account & Join'
                                        )}
                                    </Button>
                                </form>
                            </div>
                        ) : (
                            <>
                                <Button
                                    className="w-full"
                                    onClick={handleAccept}
                                >
                                    Accept & Join {inviteInfo.academyName}
                                </Button>
                            </>
                        )}
                    </div>
                )}

                {pageState === 'accepting' && (
                    <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-3" />
                        <p className="text-gray-600 dark:text-gray-400">Accepting invitation...</p>
                    </div>
                )}

                {pageState === 'success' && (
                    <div className="text-center">
                        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Welcome aboard!</h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            You've successfully joined the coaching team.
                        </p>
                        <Button className="w-full" onClick={handleGoToDashboard}>
                            Go to My Dashboard
                        </Button>
                    </div>
                )}

                {pageState === 'expired' && (
                    <div className="text-center">
                        <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Invite Expired</h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            This invitation has expired. Please ask your academy owner to send a new invite.
                        </p>
                    </div>
                )}

                {pageState === 'already_accepted' && (
                    <div className="text-center">
                        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Already Accepted</h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            This invitation has already been accepted.
                        </p>
                        <Button variant="outline" onClick={() => navigate('/dashboard')}>
                            Go to Dashboard
                        </Button>
                    </div>
                )}

                {pageState === 'error' && (
                    <div className="text-center">
                        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Invalid Invite</h1>
                        <p className="text-red-600 dark:text-red-400">{errorMsg}</p>
                    </div>
                )}
            </div>
            <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span>Powered by</span>
                <img src="/logo-gradient.png" alt="CourtSync" className="h-5 w-auto" />
                <span className="font-semibold text-gray-700 dark:text-gray-300">CourtSync</span>
            </div>
        </div>
    );
}
